/**
 * Loads one integration data document from the immutable output that rendered
 * this page. Integration components call this explicitly; this module never
 * imports, evaluates, or otherwise executes integration-supplied code.
 */

const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const DOCUMENT_KEY = /^(?=.{1,64}$)[0-9a-z][0-9a-z._-]*$/u;
const CONTENT_HASH = /^[0-9a-f]{64}$/u;
const MAX_DOCUMENT_BYTES = 512 * 1024;

interface PinnedOutputConfig {
  readonly origin: string;
  readonly outputReleaseId: string;
  readonly siteId: string;
}

interface DataDocumentManifest {
  readonly contentHash: string;
  readonly path: string;
  readonly version: number;
}

export interface IntegrationDataDocumentRequest {
  installationId: string;
  key: string;
  signal?: AbortSignal;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readPinnedOutputConfig(): PinnedOutputConfig {
  const siteId = document.body.dataset.siteId ?? "";
  const outputReleaseId = document.body.dataset.taprootOutputId ?? "";
  if (!CANONICAL_UUID.test(siteId) || !CANONICAL_UUID.test(outputReleaseId)) {
    throw new Error("Integration data is available only from a pinned immutable site output.");
  }

  return { origin: window.location.origin, outputReleaseId, siteId };
}

function requireInstallationId(installationId: string): void {
  if (!CANONICAL_UUID.test(installationId)) {
    throw new Error("Integration data document installation ID is invalid.");
  }
}

function requireDocumentKey(key: string): void {
  if (!DOCUMENT_KEY.test(key)) {
    throw new Error("Integration data document key is invalid.");
  }
}

function outputDataBaseUrl(config: PinnedOutputConfig, installationId: string): URL {
  return new URL(
    `/_taproot/outputs/${config.outputReleaseId}/integrations/${config.siteId}/${installationId}/`,
    config.origin,
  );
}

function fetchOptions(signal: AbortSignal | undefined): RequestInit {
  return {
    cache: "no-cache",
    credentials: "same-origin",
    mode: "same-origin",
    redirect: "error",
    signal,
  };
}

async function fetchRequired(url: URL, signal: AbortSignal | undefined): Promise<Response> {
  const response = await fetch(url, fetchOptions(signal));
  if (!response.ok) {
    throw new Error(`Integration data request failed with ${response.status}.`);
  }
  return response;
}

function parseJson(value: string, label: string): unknown {
  try {
    return JSON.parse(value) as unknown;
  } catch {
    throw new Error(`Integration ${label} is not valid JSON.`);
  }
}

function parseManifest(value: unknown, key: string): DataDocumentManifest {
  if (!isRecord(value)
    || typeof value.version !== "number"
    || !Number.isSafeInteger(value.version)
    || value.version <= 0
    || typeof value.path !== "string"
    || typeof value.contentHash !== "string"
    || !CONTENT_HASH.test(value.contentHash)) {
    throw new Error("Integration data manifest is invalid.");
  }

  const expectedPath = `v${value.version}/${key}.json`;
  if (value.path !== expectedPath) {
    throw new Error("Integration data manifest points outside its pinned output.");
  }

  return { version: value.version, path: value.path, contentHash: value.contentHash };
}

async function sha256Hex(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error("Integration data integrity verification is unavailable.");
  }

  const digest = await subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function parseBlob(response: Response, expectedHash: string): Promise<unknown> {
  const declaredLength = Number(response.headers.get("content-length"));
  if (Number.isFinite(declaredLength) && declaredLength > MAX_DOCUMENT_BYTES) {
    throw new Error("Integration data document exceeds the published size limit.");
  }

  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength > MAX_DOCUMENT_BYTES) {
    throw new Error("Integration data document exceeds the published size limit.");
  }
  if (await sha256Hex(bytes) !== expectedHash) {
    throw new Error("Integration data document did not match its manifest hash.");
  }

  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    throw new Error("Integration data document is not valid UTF-8 JSON.");
  }
  return parseJson(text, "data document");
}

/**
 * Resolves one JSON document through the stable manifest in this rendered
 * page's immutable output. A later pointer switch cannot affect an already
 * open page: both fetches use the one output ID read before the manifest.
 */
export async function loadIntegrationDataDocument(
  { installationId, key, signal }: IntegrationDataDocumentRequest,
): Promise<unknown> {
  requireInstallationId(installationId);
  requireDocumentKey(key);

  const config = readPinnedOutputConfig();
  const baseUrl = outputDataBaseUrl(config, installationId);
  const manifestUrl = new URL(`${key}_manifest.json`, baseUrl);
  const manifestResponse = await fetchRequired(manifestUrl, signal);
  const manifest = parseManifest(parseJson(await manifestResponse.text(), "data manifest"), key);

  // `parseManifest` admits only the literal one-segment versioned path; do
  // not resolve an arbitrary manifest URL against the page or current output.
  const blobUrl = new URL(manifest.path, baseUrl);
  if (blobUrl.origin !== config.origin || !blobUrl.pathname.startsWith(baseUrl.pathname)) {
    throw new Error("Integration data manifest points outside its pinned output.");
  }
  const blobResponse = await fetchRequired(blobUrl, signal);
  return parseBlob(blobResponse, manifest.contentHash);
}
